// services/conversationService.js
// API service for conversation history management

import config from '../config';

const API_BASE_URL = config.apiBaseUrl || 'https://qa-vblaibot.rjcorp.in/uat-bot';

console.log('ConversationService initialized with API_BASE_URL:', API_BASE_URL);

class ConversationService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get authorization headers with JWT token
   */
  getAuthHeaders() {
    const token = localStorage.getItem(config.storage.tokenKey) || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Handle API errors consistently
   */
  async handleResponse(response) {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Create a new conversation
   */
  async createConversation(title = null) {
    try {
      const url = `${this.baseURL}/api/conversations`;
      console.log('Creating conversation at:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ title })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }

  /**
   * List user's conversations
   */
  async listConversations(limit = 50, skip = 0, includeArchived = false) {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
        include_archived: includeArchived.toString()
      });

      const url = `${this.baseURL}/api/conversations?${params}`;
      console.log('Listing conversations at:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error listing conversations:', error);
      throw error;
    }
  }

  /**
   * Get full conversation details with all messages
   */
  async getConversation(conversationId) {
    try {
      const url = `${this.baseURL}/api/conversations/${conversationId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error getting conversation:', error);
      throw error;
    }
  }

  /**
   * Update conversation title
   */
  async updateTitle(conversationId, newTitle) {
    try {
      const url = `${this.baseURL}/api/conversations/${conversationId}/title`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ title: newTitle })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error updating title:', error);
      throw error;
    }
  }

  /**
   * Delete a conversation permanently
   */
  async deleteConversation(conversationId) {
    try {
      const url = `${this.baseURL}/api/conversations/${conversationId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  }

  /**
   * Pin/unpin a conversation
   */
  async pinConversation(conversationId, pinned = true) {
    try {
      const url = `${this.baseURL}/api/conversations/${conversationId}/pin`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ pinned })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('Error pinning conversation:', error);
      throw error;
    }
  }

  /**
   * Group conversations by date for display
   */
  groupConversationsByDate(conversations) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return conversations.reduce((groups, conv) => {
      const convDate = new Date(conv.updated_at);
      
      if (convDate >= today) {
        groups.today.push(conv);
      } else if (convDate >= yesterday) {
        groups.yesterday.push(conv);
      } else if (convDate >= sevenDaysAgo) {
        groups.previous7Days.push(conv);
      } else if (convDate >= thirtyDaysAgo) {
        groups.previous30Days.push(conv);
      } else {
        groups.older.push(conv);
      }
      
      return groups;
    }, {
      today: [],
      yesterday: [],
      previous7Days: [],
      previous30Days: [],
      older: []
    });
  }

  /**
   * Generate a title from the first question
   */
  generateTitle(question, maxLength = 50) {
    if (!question) return 'Untitled Conversation';
    
    let title = question.trim();
    if (title.length > maxLength) {
      title = title.substring(0, maxLength - 3) + '...';
    }
    
    return title;
  }
}

// Export singleton instance
export const conversationService = new ConversationService();
export default conversationService;

// Export the grouping function as well
export const groupConversationsByDate = (conversations) => {
  return conversationService.groupConversationsByDate(conversations);
};
